import { differenceInCalendarDays, subDays } from 'date-fns'
import { channelLabel } from './channels'
import { ATTRIBUTION_CHANNEL_LABELS } from '../components/AttributionCreditTable'

export const CHANNEL_COLUMNS = [
  { key: 'revenue', label: 'Conversion value', description: 'Revenue the selected attribution model credits to this channel. Across channels it adds up to actual revenue, except under Multi, where credit overlaps.', type: 'money' },
  { key: 'orders', label: 'Purchases', description: 'Paid orders the model credits to this channel. Fractional when a model splits one order across channels.', type: 'count' },
  { key: 'new_orders', label: 'New customers', description: 'Credit for first purchases (not renewals) given to this channel. Fractional when a model splits credit.', type: 'count' },
  { key: 'spend', label: 'Spend', description: 'Ad spend the platform reported for these dates. A channel with no ad account shows $0.', type: 'money' },
  { key: 'cac', label: 'CAC', description: 'Spend ÷ New customers. The totals row is blended — all spend ÷ all new customers — and matches the Daily Report’s Total CAC.', type: 'money', lowerIsBetter: true },
  { key: 'roas', label: 'ROAS', description: 'Conversion value ÷ Spend.', type: 'multiple' },
  { key: 'aov', label: 'AOV', description: 'Conversion value ÷ Purchases. The totals row is blended — all conversion value ÷ all purchases.', type: 'money' },
  { key: 'profit', label: 'Net revenue', description: 'Conversion value − Spend.', type: 'money' },
  // What the ad platform itself claims, from its own sync (ad_spend), beside
  // what we counted. Platforms credit by their own click and view windows and
  // count every conversion they saw, so these will not match Purchases; the
  // gap is the point. Blank for a platform whose sync does not report them.
  { key: 'platform_purchases', label: 'Platform purchases', description: 'Purchases the ad platform claims for these dates, by its own attribution (Google Ads conversions; Meta once its sync reports purchases). Will not match Purchases. Blank where the platform does not report them.', type: 'count', traffic: true },
  { key: 'platform_revenue', label: 'Platform conversion value', description: 'Conversion value the ad platform claims for these dates, by its own attribution.', type: 'money', traffic: true, hiddenByDefault: true },
  { key: 'platform_roas', label: 'Platform ROAS', description: 'Platform conversion value ÷ Spend: the return the platform reports for itself.', type: 'multiple', traffic: true, hiddenByDefault: true },
  { key: 'new_share', label: 'New-customer share', description: 'New customers ÷ Purchases.', type: 'percent', hiddenByDefault: true },
  { key: 'revenue_share', label: 'Share of conversion value', description: 'This channel’s share of total conversion value.', type: 'percent', hiddenByDefault: true, neutral: true },
  { key: 'orders_share', label: 'Share of purchases', description: 'This channel’s share of total purchases.', type: 'percent', hiddenByDefault: true, neutral: true },
  { key: 'spend_share', label: 'Share of spend', description: 'This channel’s share of total spend.', type: 'percent', hiddenByDefault: true, neutral: true },
  // Top of funnel. Impressions and clicks are the ad platform's counts
  // (ad_spend). Leads is the Daily Report's count from core. Visitors, tracked
  // leads and tracked purchases are the BigQuery funnel by FIRST touch — a
  // different rule from the one that credits sales, so each rate below says
  // which registers it divides.
  { key: 'impressions', label: 'Impressions', description: 'Ad impressions the platform reported.', type: 'count', traffic: true, hiddenByDefault: true },
  { key: 'clicks', label: 'Clicks', description: 'Ad clicks the platform reported.', type: 'count', traffic: true },
  { key: 'ctr', label: 'CTR', description: 'Clicks ÷ Impressions.', type: 'percent', traffic: true, hiddenByDefault: true },
  { key: 'cpm', label: 'CPM', description: 'Spend per 1,000 impressions.', type: 'money2', traffic: true, lowerIsBetter: true, hiddenByDefault: true },
  { key: 'cpc', label: 'CPC', description: 'Spend ÷ Clicks.', type: 'money2', traffic: true, lowerIsBetter: true, hiddenByDefault: true },
  { key: 'visitors', label: 'Unique visitors', description: 'Distinct people (browsers) who viewed a page in these dates, credited to their first-touch channel. From BigQuery.', type: 'count', traffic: true },
  // Per SESSION, credited to the channel each session landed on — not first
  // touch like Unique visitors and Leads. A bounce is a one-pageview session.
  { key: 'sessions', label: 'Sessions', description: 'Visits: a run of one person’s pageviews with no gap over 30 minutes, credited to the channel of the visit’s first page. A returning visitor who types the URL counts as Direct.', type: 'count', traffic: true },
  { key: 'bounce_rate', label: 'Bounce rate', description: 'Share of sessions that viewed only one page.', type: 'percent', traffic: true, lowerIsBetter: true },
  { key: 'intake_visitors', label: 'Intake visitors', description: 'Unique visitors who reached the intake, by first-touch channel.', type: 'count', traffic: true, hiddenByDefault: true },
  { key: 'core_leads', label: 'Leads', description: 'People who gave their contact details, by first-touch channel — the same count as the Daily Report’s Leads.', type: 'count', traffic: true },
  { key: 'cpl', label: 'CPL', description: 'Spend ÷ Leads.', type: 'money2', traffic: true, lowerIsBetter: true, hiddenByDefault: true },
  { key: 'lead_to_sale', label: 'Lead → purchase', description: 'Purchases ÷ Leads. Purchases follow the selected view while leads follow first touch, so read it as indicative.', type: 'percent', traffic: true, hiddenByDefault: true },
  { key: 'visitor_to_sale', label: 'Visitor → purchase', description: 'Purchases ÷ Unique visitors. Purchases follow the selected view while visitors follow first touch, so read it as indicative.', type: 'percent', traffic: true, hiddenByDefault: true },
  { key: 'revenue_per_visitor', label: 'Conversion value per visitor', description: 'Conversion value ÷ Unique visitors.', type: 'money2', traffic: true, hiddenByDefault: true },
  { key: 'revenue_per_lead', label: 'Conversion value per lead', description: 'Conversion value ÷ Leads.', type: 'money2', traffic: true, hiddenByDefault: true },
  { key: 'leads', label: 'Tracked leads (emails)', description: 'Visitors BigQuery saw enter an email, by first-touch channel.', type: 'count', traffic: true, hiddenByDefault: true },
  { key: 'tracked_purchases', label: 'Tracked purchases', description: 'Payments BigQuery tracked on the site, by first-touch channel. Can differ from Purchases, which come from billing.', type: 'count', traffic: true, hiddenByDefault: true },
  { key: 'lead_rate', label: 'Visitor → tracked lead', description: 'Tracked leads ÷ Unique visitors.', type: 'percent', traffic: true, hiddenByDefault: true },
  { key: 'purchase_rate', label: 'Tracked lead → purchase', description: 'Tracked purchases ÷ Tracked leads.', type: 'percent', traffic: true, hiddenByDefault: true },
]

export const DEFAULT_CHANNEL_COLUMNS = CHANNEL_COLUMNS.filter(column => !column.hiddenByDefault).map(column => column.key)

export const TRAFFIC_KEYS = ['impressions', 'clicks', 'platform_purchases', 'platform_revenue', 'visitors', 'intake_visitors', 'leads', 'tracked_purchases', 'core_leads', 'sessions', 'bounces']

const rate = (numerator, denominator, scale = 1) =>
  numerator == null || !denominator ? null : numerator / denominator * scale

/**
 * Every ratio the page derives rather than fetches. Rates are percentages; a
 * missing or zero denominator is null, never zero, so "not measured" and
 * "measured nothing" stay distinguishable.
 */
function withDerivedMetrics(row, totals) {
  const paid = row.spend > 0
  return { ...row,
    profit: row.spend == null ? null : (row.revenue ?? 0) - row.spend,
    new_share: rate(row.new_orders, row.orders, 100),
    revenue_share: totals ? rate(row.revenue, totals.revenue, 100) : 100,
    orders_share: totals ? rate(row.orders, totals.orders, 100) : 100,
    spend_share: totals ? rate(row.spend, totals.spend, 100) : row.spend > 0 ? 100 : null,
    ctr: rate(row.clicks, row.impressions, 100),
    bounce_rate: rate(row.bounces, row.sessions, 100),
    cpm: paid ? rate(row.spend, row.impressions, 1000) : null,
    cpc: paid ? rate(row.spend, row.clicks) : null,
    platform_roas: paid ? rate(row.platform_revenue, row.spend) : null,
    cpl: paid ? rate(row.spend, row.core_leads) : null,
    lead_to_sale: rate(row.orders, row.core_leads, 100),
    visitor_to_sale: rate(row.orders, row.visitors, 100),
    revenue_per_visitor: rate(row.revenue, row.visitors),
    revenue_per_lead: rate(row.revenue, row.core_leads),
    lead_rate: rate(row.leads, row.visitors, 100),
    purchase_rate: rate(row.tracked_purchases, row.leads, 100),
  }
}

/**
 * Lay the traffic counts over a snapshot's channel rows.
 *
 * Model rows use a finer vocabulary (direct, organic_search,
 * paid_branded_search…) than first-touch traffic, whose catch-all is 'other'.
 * There 'other' has no row it honestly belongs to, so it only reaches the
 * total. A channel with traffic and no sales still gets a row: it is the one
 * worth seeing.
 */
export function withChannelTraffic(snapshot, traffic) {
  if (!snapshot) return snapshot
  const byChannel = new Map((traffic?.channels || []).map(row => [row.channel, row]))
  // Before traffic arrives (or if it failed) a row keeps whatever its own API
  // already said.
  const pick = (source, own = {}) => Object.fromEntries(TRAFFIC_KEYS.map(key => [key, (traffic ? source?.[key] : own[key]) ?? null]))
  const merged = snapshot.rows.map(row => ({ ...row, ...pick(byChannel.get(row.channel), row) }))
  const known = new Set(merged.map(row => row.channel))
  for (const [channel, row] of byChannel) {
    if (known.has(channel) || channel === 'other') continue
    if (!TRAFFIC_KEYS.some(key => row[key] > 0)) continue
    merged.push({ ...emptyChannelRow(snapshot.spendKnown, channel), channel, ...pick(row) })
  }
  const totals = { ...snapshot.totals, ...pick(traffic?.totals, snapshot.totals) }
  return { ...snapshot, rows: merged.map(row => withDerivedMetrics(row, totals)), totals: blendedTotals(withDerivedMetrics(totals), snapshot.overlapping) }
}

// The totals row derives every ratio from its own sums, so each one is blended
// (all X ÷ all Y) rather than summed or averaged across channels. Under
// overlapping credit the sums of conversion value, purchases and new customers
// count one sale under several channels, so any ratio built on them is
// inflated; those stay blank, as ROAS does. Ratios of traffic and spend alone
// still hold.
const CREDIT_RATIOS = ['aov', 'roas', 'profit', 'new_share', 'lead_to_sale', 'visitor_to_sale', 'revenue_per_visitor', 'revenue_per_lead']
function blendedTotals(totals, overlapping) {
  return overlapping ? { ...totals, ...Object.fromEntries(CREDIT_RATIOS.map(key => [key, null])) } : totals
}

/**
 * Day-relative comparisons for a single-day view: yesterday, the average of the
 * last 7 days, and the average of the last 4 same weekdays. When the day is
 * today they are cut at the same clock time (`until`, services/channelCutoff),
 * so today-so-far is never measured against whole days.
 */
export const DAY_BASELINES = [
  { key: 'yesterday', label: 'Yesterday', days: day => [addDaysIso(day, -1)] },
  { key: 'last7', label: 'Avg of last 7 days', days: day => [-7, -6, -5, -4, -3, -2, -1].map(n => addDaysIso(day, n)) },
  { key: 'weekday4', label: 'Avg of last 4 same weekdays', days: day => [-28, -21, -14, -7].map(n => addDaysIso(day, n)) },
]

/**
 * Where a one-day comparison is cut, as an HH:MM ET clock (clocks compare as
 * strings). Both sides — orders, spend, visitors and leads — are cut at it.
 *
 * Today is `live`: cut at `confirmed`, the newest data BigQuery holds, so every
 * side has its visitors up to the cut; an earlier picked time cuts there
 * instead. A past day is cut at the picked time, noon by default.
 */
export function sameTimeCut({ on, isToday, confirmed, picked = null }) {
  const live = isToday && (!picked || picked >= confirmed)
  const clock = live ? confirmed : picked || '12:00'
  return { live, clock, until: on ? clock : null }
}

/** YYYY-MM-DD plus n days, as calendar days (no time zone involved). */
export function addDaysIso(day, n) {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  // eslint-disable-next-line no-restricted-syntax -- UTC noon calendar arithmetic, no zone involved
  return d.toISOString().slice(0, 10)
}

// Sums that average by dividing by the number of days. Everything else on a
// row is a ratio or a share, which a ratio of averages leaves unchanged.
const ADDITIVE_KEYS = ['revenue', 'orders', 'new_orders', 'spend']

/** A snapshot over N days, as a per-day average. Traffic is averaged by its own hook. */
export function averageSnapshot(snapshot, days) {
  if (!snapshot || !(days > 1)) return snapshot
  const scale = row => Object.fromEntries(Object.entries(row).map(([key, value]) =>
    [key, ADDITIVE_KEYS.includes(key) && typeof value === 'number' ? value / days : value]))
  return { ...snapshot, rows: snapshot.rows.map(scale), totals: scale(snapshot.totals) }
}

/** Sum traffic responses for several days into one, divided by how many. */
export function averageTraffic(responses) {
  const list = responses.filter(Boolean)
  if (!list.length) return null
  const n = list.length
  const byChannel = new Map()
  const totals = {}
  for (const data of list) {
    for (const row of data.channels || []) {
      const target = byChannel.get(row.channel) || { channel: row.channel }
      for (const key of TRAFFIC_KEYS) {
        if (row[key] == null) target[key] ??= null
        else target[key] = (target[key] || 0) + row[key] / n
      }
      byChannel.set(row.channel, target)
    }
    for (const key of TRAFFIC_KEYS) {
      const value = data.totals?.[key]
      if (value == null) totals[key] ??= null
      else totals[key] = (totals[key] || 0) + value / n
    }
  }
  const meta = { ...list[0].meta, funnel_available: list.every(d => d.meta?.funnel_available) }
  return { channels: [...byChannel.values()], totals, meta }
}

export function previousChannelPeriod(range) {
  const days = differenceInCalendarDays(range.to, range.from) + 1
  return { from: subDays(range.from, days), to: subDays(range.from, 1) }
}

/**
 * Move a column to where another one sits, shifting the rest along — what a
 * drag does. Hidden columns keep their places relative to each other.
 */
export function moveColumn(order, key, target) {
  const from = order.indexOf(key)
  const to = order.indexOf(target)
  if (from < 0 || to < 0 || from === to) return order
  const next = order.filter(item => item !== key)
  next.splice(next.indexOf(target) + (from < to ? 1 : 0), 0, key)
  return next
}

export function normalizeChannelColumns(value) {
  const known = new Set(CHANNEL_COLUMNS.map(column => column.key))
  const order = [...new Set([...(Array.isArray(value?.order) ? value.order : []), ...known])].filter(key => known.has(key))
  const visible = Array.isArray(value?.visible) ? [...new Set(value.visible.filter(key => known.has(key)))] : DEFAULT_CHANNEL_COLUMNS
  return { order, visible: visible.length ? visible : DEFAULT_CHANNEL_COLUMNS }
}

const dollars = value => value == null ? null : value / 100

// The totals row's CAC is BLENDED — all ad spend over new sales — and comes from
// the server computed exactly as the Daily Report's Total CAC, so the two
// reports show one number. Null when it could not be computed, never a
// differently-defined stand-in.
const blendedCac = data => dollars(data?.blended?.cac ?? null)

// The engine reports money in cents; the table shows dollars. Adapt once.
export function channelReportSnapshot(data, engine) {
  if (!data?.models) return null
  const model = data.models.find(item => item.id === engine)
  if (!model) return null
  const rows = model.rows.map(row => ({ ...row,
    revenue: dollars(row.revenue), spend: dollars(row.spend), aov: dollars(row.aov), cac: dollars(row.cac),
  }))
  const sum = key => rows.reduce((total, row) => total + (row[key] || 0), 0)
  // Overlapping credit (Multi) sums past actual sales, so ratios of credit have
  // no honest total there.
  const reconciles = model.reconciles !== false
  return {
    label: model.label, model, rows, overlapping: model.reconciles === false,
    spendKnown: data.spend != null,
    totals: { revenue: sum('revenue'), orders: sum('orders'), new_orders: sum('new_orders'),
      spend: rows.some(row => row.spend != null) ? sum('spend') : null, cac: blendedCac(data),
      roas: reconciles && sum('spend') > 0 ? sum('revenue') / sum('spend') : null,
      aov: reconciles && sum('orders') > 0 ? sum('revenue') / sum('orders') : null },
  }
}

export function channelReportRows(current, comparison) {
  const a = new Map(current.rows.map(row => [row.channel, row]))
  const b = new Map((comparison?.rows || []).map(row => [row.channel, row]))
  return [...new Set([...a.keys(), ...b.keys()])].map(channel => ({
    channel,
    label: a.get(channel)?.channel_label || b.get(channel)?.channel_label || ATTRIBUTION_CHANNEL_LABELS[channel] || channelLabel(channel),
    current: a.get(channel) || emptyChannelRow(current.spendKnown, channel),
    comparison: comparison ? b.get(channel) || emptyChannelRow(comparison.spendKnown, channel) : null,
  }))
}

function emptyChannelRow(spendKnown, channel) {
  return { revenue: 0, orders: 0, new_orders: 0,
    spend: spendKnown && !['unattributed', 'outside_window'].includes(channel) ? 0 : null, aov: null, cac: null, roas: null,
    ...Object.fromEntries(CHANNEL_COLUMNS.filter(column => column.traffic).map(column => [column.key, null])) }
}

export function channelDelta(current, previous) {
  if (current == null || previous == null) return null
  const absolute = current - previous
  return { absolute, percent: previous === 0 ? null : absolute / Math.abs(previous) * 100 }
}

export function sortChannelRows(rows, { key, direction }) {
  return [...rows].sort((a, b) => {
    if (key === 'channel') return (direction === 'asc' ? 1 : -1) * a.label.localeCompare(b.label)
    const av = a.current[key]
    const bv = b.current[key]
    // Unavailable always stays below measured values, including real zeroes.
    if (av == null && bv != null) return 1
    if (bv == null && av != null) return -1
    return (direction === 'asc' ? 1 : -1) * ((av ?? 0) - (bv ?? 0)) || a.label.localeCompare(b.label)
  })
}

export function formatChannelMetric(value, type) {
  if (value == null || !Number.isFinite(value)) return '—'
  if (type === 'money') return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  if (type === 'money2') return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
  if (type === 'percent') return `${value.toFixed(1)}%`
  if (type === 'multiple') return `${value.toFixed(2)}×`
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)
}

/** Columns a campaign or content row under a channel can fill; the rest stay blank. */
export const CAMPAIGN_ROW_KEYS = new Set(['revenue', 'orders', 'new_orders', 'spend', 'cac', 'roas', 'aov', 'profit', 'new_share', 'clicks', 'impressions', 'ctr', 'cpm', 'cpc', 'platform_purchases', 'platform_revenue', 'platform_roas'])

const EPSILON = 1e-6

function campaignMetrics(row) {
  const paid = row.spend > 0
  return { ...row,
    cac: paid ? rate(row.spend, row.new_orders) : null,
    roas: paid ? rate(row.revenue, row.spend) : null,
    aov: rate(row.revenue, row.orders),
    profit: row.spend == null ? null : (row.revenue ?? 0) - row.spend,
    new_share: rate(row.new_orders, row.orders, 100),
    ctr: rate(row.clicks, row.impressions, 100),
    cpm: paid ? rate(row.spend, row.impressions, 1000) : null,
    cpc: paid ? rate(row.spend, row.clicks) : null,
    platform_purchases: row.platform_purchases ?? null,
    platform_revenue: row.platform_revenue ?? null,
    platform_roas: paid ? rate(row.platform_revenue, row.spend) : null,
  }
}

/**
 * The model's credit by campaign → content, keyed by channel, from
 * /marketing/campaigns/channel-attribution (dollars).
 *
 * A channel's campaigns sum to its row. A campaign's contents may not: credit
 * its touch proved only to the campaign (every Google click, an email link
 * with no utm_content) gets its own "Credited to the campaign only" row so the
 * difference is visible rather than missing. Campaigns ad_spend does not know
 * have no spend; under a channel whose row spent $0 that is a real $0.
 */
export function channelCampaignRows(data, spendOf = () => null) {
  if (!data?.channels) return null
  const byChannel = new Map()
  for (const { channel, campaigns } of data.channels) {
    const channelSpend = spendOf(channel)
    const spendFor = row => row.spend ?? (channelSpend === 0 ? 0 : null)
    byChannel.set(channel, campaigns.map(campaign => {
      const contents = campaign.contents.map(content => campaignMetrics({ ...content, spend: spendFor(content) }))
        .sort((a, b) => (b.revenue - a.revenue) || ((b.spend || 0) - (a.spend || 0)))
      const placed = contents.reduce((sum, item) => ({ orders: sum.orders + item.orders, revenue: sum.revenue + item.revenue, new_orders: sum.new_orders + item.new_orders }), { orders: 0, revenue: 0, new_orders: 0 })
      const rest = { orders: campaign.orders - placed.orders, revenue: campaign.revenue - placed.revenue, new_orders: campaign.new_orders - placed.new_orders }
      if (contents.length && (rest.orders > EPSILON || rest.revenue > EPSILON)) {
        contents.push(campaignMetrics({ key: '__campaign_only__', label: null, campaignOnly: true, ...rest, spend: null }))
      }
      const label = campaign.key === data.no_campaign_key ? null : campaign.label
      return campaignMetrics({ ...campaign, label, noCampaign: campaign.key === data.no_campaign_key, spend: spendFor(campaign), contents })
    }).sort((a, b) => (b.revenue - a.revenue) || ((b.spend || 0) - (a.spend || 0))))
  }
  return byChannel
}
